import { MenuProps } from 'antd';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useScreenSize } from '../../../hooks/useScreenSize';
import { DesktopSider } from './desktop';
import { MobileSider } from './mobile';
import {
    ICCar,
    ICReceipt,
    ICReservationCalendar,
    ICReport,
    ICService,
    ICReservationImport,
    ICUsers,
} from '../../../icons';
import { ICBilling } from '../../../icons/billing';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/reducers';
import { isOrgOwner } from '../../../utils/constants';
import { ICAllWorkOrder } from '../../../icons/all-work-order';
import { ICWorkOrder } from '../../../icons/work-order';

const Wrapper = styled.div`
    .ant-menu-item,
    .ant-menu-submenu-title {
        gap: 0.5rem;
    }
`;

type MenuItem = Required<MenuProps>['items'][number];

type SiderAdminProps = {
    collapsed: boolean;
    setCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
};

export const SiderAdmin: React.FC<SiderAdminProps> = ({
    collapsed,
    setCollapsed,
}) => {
    const size = useScreenSize();
    const mobileMode = size === 'xs' || size === 'sm';
    const navigate = useNavigate();
    const [selectedMenu, setSelectedMenu] = React.useState('');
    const [openKeys, setOpenKeys] = React.useState(['']);
    const userInfo = useSelector((state: RootState) => state.userInfo);

    const items: MenuItem[] = React.useMemo(() => {
        const isManagerRole = isOrgOwner(userInfo?.role);
        if (!isManagerRole) {
            return [
                {
                    key: 'work_order',
                    icon: (
                        <span
                            role="img"
                            aria-label="work-order"
                            className="anticon anticon-user ant-menu-item-icon"
                        >
                            <ICWorkOrder width={30} height={30} />
                        </span>
                    ),
                    label: 'My Work Orders',
                },
                {
                    key: 'add_receipts',
                    icon: (
                        <span
                            role="img"
                            aria-label="receipt"
                            className="anticon anticon-user ant-menu-item-icon"
                        >
                            <ICReceipt width={30} height={30} />
                        </span>
                    ),
                    label: 'Add Receipt',
                },
            ];
        }
        return [
            {
                key: 'work_order',
                icon: (
                    <span
                        role="img"
                        aria-label="work-order"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICWorkOrder width={30} height={30} />
                    </span>
                ),
                label: 'My Work Orders',
            },
            {
                key: 'all_work_orders',
                icon: (
                    <span
                        role="img"
                        aria-label="all-work-orders"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICAllWorkOrder width={30} height={30} />
                    </span>
                ),
                label: 'All Work Orders',
            },
            {
                key: 'add_receipts',
                icon: (
                    <span
                        role="img"
                        aria-label="receipt"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICReceipt width={30} height={30} />
                    </span>
                ),
                label: 'Add Receipt',
            },
            {
                key: 'report',
                icon: (
                    <span
                        role="img"
                        aria-label="user"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICReport width={30} height={30} />
                    </span>
                ),
                label: 'Report',
            },
            {
                key: 'shops',
                icon: (
                    <span
                        role="img"
                        aria-label="shops"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICService width={30} height={30} />
                    </span>
                ),
                label: 'Shops',
            },
            {
                key: 'cars',
                icon: (
                    <span
                        role="img"
                        aria-label="cars"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICCar width={30} height={30} />
                    </span>
                ),
                label: 'Cars',
            },
            {
                key: 'services',
                icon: (
                    <span
                        role="img"
                        aria-label="services"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICService width={30} height={30} />
                    </span>
                ),
                label: 'Services',
            },
            {
                key: 'users',
                icon: (
                    <span
                        role="img"
                        aria-label="users"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICUsers width={30} height={30} />
                    </span>
                ),
                label: 'Users',
            },
            {
                key: 'turo_reservations',
                icon: (
                    <span
                        role="img"
                        aria-label="turo"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICReservationImport width={30} height={30} />
                    </span>
                ),
                label: 'Import Reservation',
            },
            {
                key: 'reservation_calendar',
                icon: (
                    <span
                        role="img"
                        aria-label="reservation-calendar"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICReservationCalendar width={30} height={30} />
                    </span>
                ),
                label: 'Reservation Calendar',
            },
            {
                key: 'billing',
                icon: (
                    <span
                        role="img"
                        aria-label="billing"
                        className="anticon anticon-user ant-menu-item-icon"
                    >
                        <ICBilling width={30} height={30} />
                    </span>
                ),
                label: 'Billing',
            },
        ];
    }, [userInfo]);

    const onClickMenu: MenuProps['onClick'] = ({ key }) => {
        setSelectedMenu(key);
        setCollapsed(false);
        if (key === 'all_work_orders') {
            navigate('/all-work-orders');
        }
        if (key === 'work_order') {
            navigate('/work-order');
        }
        if (key === 'start_service') {
            navigate('/start-service');
        }
        if (key === 'add_receipts') {
            navigate('/add-receipts');
        }
        if (key === 'shops') {
            navigate('/settings/shops');
        }
        if (key === 'cars') {
            navigate('/settings/cars');
        }
        if (key === 'services') {
            navigate('/settings/services');
        }
        if (key === 'users') {
            navigate('/settings/user-service');
        }
        if (key === 'turo_reservations') {
            navigate('/settings/turo-reservations');
        }
        if (key === 'reservation_calendar') {
            navigate('/reservation-calendar');
        }
        if (key === 'billing') {
            navigate('/billing');
        }
        if (key === 'report') {
            navigate('/report');
        }
    };

    React.useEffect(() => {
        const path = window.location.pathname;
        setSelectedMenu('');
        if (path.includes('/all-work-orders')) {
            setSelectedMenu('all_work_orders');
        }
        if (path.includes('/work-order')) {
            setSelectedMenu('work_order');
        }
        if (path === '/start-service') {
            setSelectedMenu('start_service');
        }
        if (path === '/add-receipts') {
            setSelectedMenu('add_receipts');
        }
        if (path === '/settings/shops') {
            setSelectedMenu('shops');
        }
        if (path === '/settings/cars') {
            setSelectedMenu('cars');
        }
        if (path === '/settings/services') {
            setSelectedMenu('services');
        }
        if (path === '/settings/user-service') {
            setSelectedMenu('users');
        }
        if (path === '/settings/turo-reservations') {
            setSelectedMenu('turo_reservations');
        }
        if (path === '/report') {
            setSelectedMenu('report');
        }
        if (path === '/reservation-calendar') {
            setSelectedMenu('reservation_calendar');
        }
        if (path === '/billing') {
            setSelectedMenu('billing');
        }
    }, []);

    React.useEffect(() => {
        if (!items.find((item: any) => item.key === window.location.pathname)) {
            const currentSubItem = items.find((item: any) =>
                item.children?.find((item: any) => {
                    return window.location.pathname.includes(item.key);
                })
            ) as any;
            if (currentSubItem) {
                setOpenKeys([(currentSubItem['key'] as string) ?? '']);
                if (currentSubItem && currentSubItem['children']) {
                    const currentSubItemKey = currentSubItem['children']?.find(
                        (item: any) => {
                            return window.location.pathname.includes(item.key);
                        }
                    );
                    if (currentSubItemKey) {
                        setSelectedMenu(
                            (currentSubItemKey['key'] as string) ?? ''
                        );
                    }
                }
            }
            return;
        }
        setOpenKeys([]);
    }, [items]);

    return (
        <Wrapper className="flex">
            {mobileMode ? (
                <MobileSider
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    openKeys={openKeys}
                    items={items}
                    selectedMenu={selectedMenu}
                    onClickMenu={onClickMenu}
                    setOpenKeys={setOpenKeys}
                />
            ) : (
                <DesktopSider
                    collapsed={collapsed}
                    setCollapsed={setCollapsed}
                    openKeys={openKeys}
                    items={items}
                    selectedMenu={selectedMenu}
                    onClickMenu={onClickMenu}
                    setOpenKeys={setOpenKeys}
                />
            )}
        </Wrapper>
    );
};
